import { namesMatch } from '@ltvco/refresh-lib/utils';
import { searchReport } from '@ltvco/refresh-lib/v1';
import { yupResolver } from '@ltvco/refresh-lib/vendors';
import * as yup from 'yup';

export type VehicleSearchRequestBody = {
  report_type: 'vehicle_report';
  meta: {
    vin?: string;
    tag?: string;
    year?: string;
    make?: string;
    model?: string;
    trim?: string;
    licensePlate?: string;
    state?: string;
    report_flags: unknown[];
  };
};

export interface SearchParams {
  tag?: string;
  vin?: string;
  make?: string;
  model?: string;
  trim?: string;
  year?: string;
  licensePlate?: string;
  state?: string;
  ddPlan?: string;
}

export interface VehicleFormData {
  vin: string;
  licensePlate: string;
  state: string;
  year: string;
  make: string;
  model: string;
  trim: string;
  searchType: VehicleSearchType;
  /** Optional for shared forms (e.g. Recalls) that add a query field */
  query?: string;
}

export enum VehicleSearchType {
  // eslint-disable-next-line no-unused-vars
  VIN = 'VIN',
  // eslint-disable-next-line no-unused-vars
  LICENSE_PLATE = 'LICENSE_PLATE',
  // eslint-disable-next-line no-unused-vars
  YEAR_MAKE_MODEL = 'YEAR_MAKE_MODEL',
}

const STANDARD_VIN_LENGTH = 17;
const MIN_NON_STANDARD_VIN_LENGTH = 5;
const MAX_NON_STANDARD_VIN_LENGTH = 16;
const ALPHANUMERIC_REGEX = new RegExp('^[a-zA-Z0-9]+$');
const VIN_VALIDATION_REGEX = new RegExp(
  '^[A-HJ-NPR-Z0-9]{8}[0-9xX][A-HJ-NPR-Z0-9]{8}$'
);
const LICENSE_PLATE_VALIDATION_REGEX = new RegExp(
  '^(?=.*[a-zA-Z0-9])[a-zA-Z0-9-]{1,10}$'
);

export const searchTypeOptions = [
  { value: VehicleSearchType.VIN, label: 'VIN' },
  { value: VehicleSearchType.LICENSE_PLATE, label: 'License Plate' },
  { value: VehicleSearchType.YEAR_MAKE_MODEL, label: 'Year, Make, Model' },
];

export const getPlaceholderTextForVehicleSearch = (
  searchType: VehicleSearchType,
  isMobile: boolean
) => {
  switch (searchType) {
    case VehicleSearchType.VIN:
      return isMobile ? 'VIN Number' : 'e.g. 1g1ze51234567';
    case VehicleSearchType.LICENSE_PLATE:
      return isMobile ? 'License Plate' : 'e.g. 12345';
    case VehicleSearchType.YEAR_MAKE_MODEL:
      return '';
    default:
      return 'Enter vehicle information';
  }
};

export const schemaVinSearchForm = yup
  .object({
    vin: yup
      .string()
      .required("We'll need a valid VIN to do a search.")
      .max(17)
      .test(
        'vin-validation',
        "We'll need a valid VIN to do a search.",
        (value) => {
          return isValidVIN(value);
        }
      ),
  })
  .required();

export const schemaPlateSearchForm = yup
  .object({
    licensePlate: yup
      .string()
      .required("We'll need a valid License Plate to do a search.")
      .max(10)
      .test(
        'license-plate-validation',
        "We'll need a valid License Plate to do a search.",
        (value) => {
          return isValidLicensePlate(value);
        }
      ),
    state: yup.string().nullable().max(2).required('Select a state.'),
  })
  .required();

export const schemaYmmSearchForm = yup
  .object({
    year: yup.string().required('Select a valid year.'),
    make: yup.string().required('Select a valid make.'),
    model: yup.string().required('Select a valid model.'),
    trim: yup.string().required('Select a valid trim.'),
  })
  .required();

// Get resolver based on search type
export const getResolver = (searchType: VehicleSearchType) => {
  switch (searchType) {
    case VehicleSearchType.VIN:
      return yupResolver(schemaVinSearchForm);
    case VehicleSearchType.LICENSE_PLATE:
      return yupResolver(schemaPlateSearchForm);
    case VehicleSearchType.YEAR_MAKE_MODEL:
      return yupResolver(schemaYmmSearchForm);
  }
};

// Utility function for states
export const getAbbrStatesAsArray = (): string[] => {
  return [
    'All',
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
    'DC',
  ];
};

export async function vehicleSearch(searchParams: SearchParams) {
  const { vin, tag, make, year, trim, model, licensePlate, state, ddPlan } =
    searchParams;
  const body: VehicleSearchRequestBody = {
    report_type: 'vehicle_report',
    meta: {
      report_flags: ddPlan ? [`dd_plan%3D${ddPlan}`] : [],
      ...(vin && { vin }),
      ...(tag && { tag }),
      ...(trim && { trim }),
      ...(make && year && model && { make, year, model }),
      ...(licensePlate && state && { license_plate: licensePlate, state }),
    },
  };

  return await searchReport(body);
}

// Utility functions for Year, Make, Model, Trim
export interface Make {
  id: string;
  name: string;
  models: { id: string; name: string; trims: { id: string; name: string }[] }[];
}

export function getYears() {
  const years = [];
  const startYear = 1980;
  let nextYear = new Date().getFullYear() + 1;
  while (nextYear >= startYear) {
    years.push((nextYear--).toString());
  }
  return years;
}

export function getMakes(makesObj: Make[]): string[] {
  return makesObj.map((make) => {
    return make.name;
  });
}

export function getModels(makeName: string, makes: Make[]): string[] {
  if (makes && makeName) {
    const make = makes.find((make) => namesMatch(make.name, makeName));
    if (make && make.models) {
      return make.models.map((model) => {
        return model.name;
      });
    }
  }
  return [];
}

export function getVehicleTrims(
  makeName: string,
  modelName: string,
  makes: Make[]
): string[] {
  if (makes && makeName && modelName) {
    const make = makes.find((make) => namesMatch(make.name, makeName));
    if (make && make.models) {
      const model = make.models.find((model) =>
        namesMatch(model.name, modelName)
      );
      if (model && model.trims) {
        return model.trims.map((trim) => {
          return trim.name;
        });
      }
    }
  }
  return [];
}

function checkDigitCheck(vin: string): boolean {
  const transliteration: { [key: string]: number } = {
    A: 1,
    B: 2,
    C: 3,
    D: 4,
    E: 5,
    F: 6,
    G: 7,
    H: 8,
    J: 1,
    K: 2,
    L: 3,
    M: 4,
    N: 5,
    P: 7,
    R: 9,
    S: 2,
    T: 3,
    U: 4,
    V: 5,
    W: 6,
    X: 7,
    Y: 8,
    Z: 9,
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    0: 0,
  };

  const weights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

  const chars = vin.split('');
  const product = chars.reduce((total, char, index) => {
    return total + transliteration[char] * weights[index];
  }, 0);

  let expectedCheckDigit: number | 'X' = product % 11;
  if (expectedCheckDigit === 10) {
    expectedCheckDigit = 'X';
  }

  return expectedCheckDigit.toString() === chars[8];
}

export function isValidVIN(input: string | undefined): boolean {
  if (input) {
    input = input.toUpperCase().trim();

    // Standard VINs
    if (input.length === STANDARD_VIN_LENGTH) {
      return VIN_VALIDATION_REGEX.test(input) && checkDigitCheck(input);
    }

    // Non-standard VINs
    if (
      input.length >= MIN_NON_STANDARD_VIN_LENGTH &&
      input.length <= MAX_NON_STANDARD_VIN_LENGTH
    ) {
      return ALPHANUMERIC_REGEX.test(input);
    }

    return false;
  }

  return false;
}

export function isValidLicensePlate(input: string | undefined): boolean {
  if (input) {
    input = input.toUpperCase().trim();
    return LICENSE_PLATE_VALIDATION_REGEX.test(input);
  }

  return false;
}
